import React, { useEffect, useRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Message, MessageStep } from './types';
import ApprovalRequestCard from './ApprovalRequestCard';
import ChatMarkdown from '../../ChatMarkdown/ChatMarkdown';

interface ChatMessageProps {
  message: Message;
  onApproval: (messageId: string, approved: boolean) => void;
  isLoading?: boolean;
  /** ID of the message whose approval is currently being processed */
  approvingMessageId?: string | null;
}

function formatToolOutput(output: MessageStep['output']): string {
  if (output == null) return '';
  if (typeof output === 'string') return output;
  if (Array.isArray(output)) return JSON.stringify(output, null, 2);
  return JSON.stringify(output, null, 2);
}

function StepItem({ step }: { step: MessageStep }) {
  const theme = useTheme();
  const secondary =
    theme.customColors?.themeNeutralSecondaryColor || 'text.secondary';

  if (step.kind === 'tool_call') {
    const label = step.message ?? `Calling ${step.tool ?? 'tool'}…`;
    return (
      <Typography variant="caption" sx={{ color: secondary }}>
        {label}
      </Typography>
    );
  }
  if (step.kind === 'message') {
    return (
      <Typography
        variant="caption"
        sx={{ color: secondary, whiteSpace: 'pre-wrap' }}
      >
        {step.content ?? ''}
      </Typography>
    );
  }
  if (step.kind === 'tool_output') {
    const output = formatToolOutput(step.output);
    return (
      <Box>
        {output && (
          <Typography
            variant="caption"
            sx={{
              color: secondary,
              display: 'block',
              mt: 0.25,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '0.7rem'
            }}
          >
            {output.length > 200 ? output.slice(0, 200) + '…' : output}
          </Typography>
        )}
      </Box>
    );
  }
  return null;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onApproval,
  isLoading = false,
  approvingMessageId
}) => {
  const theme = useTheme();
  const progressScrollRef = useRef<HTMLDivElement>(null);
  const showThinking =
    !message.isUser && !message.text && !message.steps?.length && isLoading;
  const hasSteps = message.steps && message.steps.length > 0;

  useEffect(() => {
    if (hasSteps && progressScrollRef.current) {
      progressScrollRef.current.scrollTop =
        progressScrollRef.current.scrollHeight;
    }
  }, [hasSteps, message.steps?.length]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: message.isUser ? 'flex-end' : 'flex-start',
        gap: 0.5
      }}
    >
      {showThinking && (
        <Box
          sx={{
            p: 1,
            color:
              theme.customColors?.themeNeutralSecondaryColor ||
              'text.secondary',
            '@keyframes blink': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.4 }
            },
            animation: 'blink 1.2s ease-in-out infinite'
          }}
        >
          <Typography variant="body2">thinking . . .</Typography>
        </Box>
      )}
      {hasSteps && (
        <Box
          sx={{
            maxWidth: '100%',
            maxHeight: 180,
            p: 1,
            borderRadius: '8px',
            backgroundColor: 'action.hover',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            overflow: 'hidden',
            mt: 0.5
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, flexShrink: 0 }}
          >
            Progress
          </Typography>
          <Box
            ref={progressScrollRef}
            sx={{
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              '& .MuiTypography-root': { fontSize: '0.7rem' },
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {message.steps!.map((step, i) => (
              <StepItem key={i} step={step} />
            ))}
          </Box>
        </Box>
      )}
      {message.text && (
        <Box
          sx={{
            maxWidth: '100%',
            p: 1,
            borderRadius: '8px',
            backgroundColor: message.isUser ? '#424242' : 'transparent',
            color: message.isUser
              ? 'white'
              : theme.customColors?.themeNeutralPrimaryColor || 'text.primary',
            '& .chatmarkdown-container': {
              fontSize: theme.typography.caption.fontSize,
              wordBreak: 'break-word' as const,
              '& a': {
                color: message.isUser
                  ? '#90caf9'
                  : theme.customColors?.themePrimary || 'primary.main'
              },
              // Compact list styling for narrow chat area
              '& ul, & ol': {
                paddingLeft: 1,
                margin: 1
              },
              '& li': {
                marginBottom: 0
              },
              '& li > ul, & li > ol': {
                paddingLeft: 0.5,
                marginTop: 0
              }
            }
          }}
        >
          <ChatMarkdown>{message.text.replace(/\n/g, '  \n')}</ChatMarkdown>
        </Box>
      )}

      {message.approvalRequests && (
        <ApprovalRequestCard
          approvalRequests={message.approvalRequests}
          approvalStatus={message.approvalStatus}
          messageId={message.id}
          onApproval={onApproval}
          isApprovingLoading={approvingMessageId === message.id}
        />
      )}
    </Box>
  );
};

export default ChatMessage;
